// react
import React, { useState } from "react";

// prop-types
import PropTypes from "prop-types";

// react-i18next
import { useTranslation } from "react-i18next";

// @mui
import {
  Box,
  Grid,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TableContainer,
  TableFooter,
  TablePagination,
  Button,
  IconButton,
  Typography,
  useMediaQuery,
  useTheme,
  Tooltip,
} from "@mui/material";
import {
  KeyboardArrowLeftOutlined as KeyboardArrowLeftOutlinedIcon,
  KeyboardArrowRightOutlined as KeyboardArrowRightOutlinedIcon,
  VisibilityOff as VisibilityOffIcon,
  Visibility as VisibilityIcon,
  Add as AddIcon,
} from "@mui/icons-material";

// utils
import convertTimestamp from "utils/convertTimestamp";

// A ---------------------------------------------------------------------- M

const DataKeysTableToolbar = ({ handleOpen }) => {
  const { t } = useTranslation();

  return (
    <Grid container alignItems="center" spacing={2} mb={2}>
      <Grid item xs={12} sm={6}>
        <Typography variant="body1" fontWeight={700} color="text.secondary">
          {t("datakeys")}
        </Typography>
      </Grid>

      <Grid item xs={12} sm={6} container justifyContent="flex-end" spacing={1}>
        <Grid item sm="auto">
          <Button
            onClick={handleOpen}
            startIcon={<AddIcon />}
            variant="contained"
            sx={{
              borderRadius: 3,
              textTransform: "none",
              fontWeight: 600,
              px: 3,
            }}
          >
            {t("datakey")}
          </Button>
        </Grid>
      </Grid>
    </Grid>
  );
};

const TablePaginationActions = ({ count, page, rowsPerPage, onPageChange }) => {
  const theme = useTheme();
  const isRtl = theme.direction === "rtl";

  return (
    <Box sx={{ flexShrink: 0, ml: 2.5 }}>
      <IconButton onClick={(e) => onPageChange(e, page - 1)} disabled={page === 0} aria-label="previous page">
        {isRtl ? <KeyboardArrowRightOutlinedIcon /> : <KeyboardArrowLeftOutlinedIcon />}
      </IconButton>
      <IconButton onClick={(e) => onPageChange(e, page + 1)} disabled={page >= Math.ceil(count / rowsPerPage) - 1} aria-label="next page">
        {isRtl ? <KeyboardArrowLeftOutlinedIcon /> : <KeyboardArrowRightOutlinedIcon />}
      </IconButton>
    </Box>
  );
};

TablePaginationActions.propTypes = {
  count: PropTypes.number.isRequired,
  onPageChange: PropTypes.func.isRequired,
  page: PropTypes.number.isRequired,
  rowsPerPage: PropTypes.number.isRequired,
};

const DataRow = ({ datakey }) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery((theme) => theme.breakpoints.down("sm"));

  const [showDataKey, setShowDataKey] = useState(false);

  const toggleDataKeyVisibility = () => {
    setShowDataKey(!showDataKey);
  };

  if (isMobile) {
    return (
      <Box
        key={datakey.id}
        sx={{
          p: 2,
          mb: 2,
          mx: 1,
          borderRadius: 3,
          background: `linear-gradient(135deg, ${theme.palette.background.paper}, ${theme.palette.background.default}EE)`,
          border: `1px solid ${theme.palette.divider}`,
          boxShadow: "0 2px 10px rgba(0,0,0,0.2)",
          cursor: "pointer",
          transition: "transform 0.2s ease, box-shadow 0.2s ease",
          "&:hover": {
            transform: "scale(1.01)",
            boxShadow: "0 4px 16px rgba(0,0,0,0.3)",
          },
        }}
      >
        <Typography variant="subtitle1" fontWeight={700}>
          {datakey.name || "N/A"}
        </Typography>

        <Typography variant="body2" color="text.secondary">
          {datakey.created_on ? convertTimestamp(datakey.created_on) : "N/A"}
        </Typography>

        <Box mt={1} sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <Typography variant="body2" sx={{ wordBreak: "break-all" }}>
            {showDataKey ? datakey.datakey : "••••••"}
          </Typography>
          <Box display="flex">
            <Tooltip title={showDataKey ? t("hide") : t("show")} placement="top">
              <IconButton onClick={toggleDataKeyVisibility} color="primary">
                {showDataKey ? <VisibilityOffIcon /> : <VisibilityIcon />}
              </IconButton>
            </Tooltip>
          </Box>
        </Box>
      </Box>
    );
  }

  return (
    <TableRow>
      <TableCell align="left">{datakey.name || "N/A"}</TableCell>
      <TableCell align="left">{datakey.created_on ? convertTimestamp(datakey.created_on) : "N/A"}</TableCell>
      <TableCell align="left" size="small">
        <span style={{ marginRight: "10px" }}>{showDataKey ? datakey.datakey : "••••••"}</span>
        <Tooltip title={showDataKey ? t("hide") : t("show")} placement="top">
          <IconButton onClick={toggleDataKeyVisibility} color="primary">
            {showDataKey ? <VisibilityOffIcon /> : <VisibilityIcon />}
          </IconButton>
        </Tooltip>
      </TableCell>
    </TableRow>
  );
};

const DataKeysTable = ({ dataKeys, handleOpen }) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery((theme) => theme.breakpoints.down("sm"));

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const emptyRows = page > 0 ? Math.max(0, (1 + page) * rowsPerPage - dataKeys.length) : 0;

  return (
    <Box>
      <DataKeysTableToolbar handleOpen={handleOpen} />

      <TableContainer
        component={Paper}
        elevation={0}
        sx={{
          borderRadius: 3,
          overflow: "hidden",
          background: `linear-gradient(135deg, ${theme.palette.background.paper}, ${theme.palette.background.default}CC)`,
          backdropFilter: "blur(6px)",
          border: isMobile ? "none" : `1px solid ${theme.palette.divider}`,
        }}
      >
        <Table data-testid="datakeys-table">
          {!isMobile && (
            <TableHead sx={{ background: `linear-gradient(90deg, ${theme.palette.primary.main}33, ${theme.palette.primary.dark}11)` }}>
              <TableRow>
                <TableCell sx={{ fontWeight: 700 }}>{t("name")}</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>{t("creation_datetime")}</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>{t("key")}</TableCell>
              </TableRow>
            </TableHead>
          )}

          <TableBody>
            {(rowsPerPage > 0 ? dataKeys.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage) : dataKeys).map((datakey) => {
              return <DataRow key={datakey.id} datakey={datakey} />;
            })}
            {!isMobile && emptyRows > 0 && (
              <TableRow style={{ height: 53 * emptyRows }}>
                <TableCell colSpan={3} />
              </TableRow>
            )}
          </TableBody>

          {!isMobile && (
            <TableFooter>
              <TableRow>
                <TablePagination
                  rowsPerPageOptions={[10, 20]}
                  count={dataKeys.length}
                  rowsPerPage={rowsPerPage}
                  page={page}
                  onPageChange={handleChangePage}
                  onRowsPerPageChange={handleChangeRowsPerPage}
                  labelRowsPerPage={t("rows_per_page")}
                  labelDisplayedRows={({ from, to, count }) => t("displayed_rows", { from, to, count: count !== -1 ? count : `more than ${to}` })}
                  ActionsComponent={TablePaginationActions}
                />
              </TableRow>
            </TableFooter>
          )}
        </Table>

        {isMobile && (
          <Box sx={{ display: "flex", justifyContent: "center", mt: 2 }}>
            <TablePaginationActions count={dataKeys.length} page={page} rowsPerPage={rowsPerPage} onPageChange={handleChangePage} />
          </Box>
        )}
      </TableContainer>
    </Box>
  );
};

DataKeysTable.propTypes = {
  dataKeys: PropTypes.array.isRequired,
  handleOpen: PropTypes.func.isRequired,
};

export default DataKeysTable;
